"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// Sources:
//   • CDC Vaccines for Children (VFC) Storage and Handling Toolkit (2024):
//     vaccine refrigerator 2°C to 8°C (36°F to 46°F); freezer −50°C to −15°C
//     (−58°F to +5°F); purpose-built or pharmaceutical-grade only;
//     stand-alone units required (no dorm/bar units; no household
//     combination units for vaccine storage).
//     https://www.cdc.gov/vaccines/hcp/admin/storage/toolkit/storage-handling-toolkit.pdf
//   • NSF/ANSI 456 — Vaccine Storage: dimensional, temperature uniformity,
//     and recovery requirements for purpose-built vaccine refrigerators.
//   • FDA Compliance Policy Guide / 21 CFR 600.15 — biologics and blood
//     products storage; AABB Standards for blood banks.
//   • USP <797> Pharmaceutical Compounding — Sterile Preparations:
//     refrigerated storage 2°C–8°C for compounded sterile preparations.
//   • CLIA / CAP laboratory accreditation: continuous temperature monitoring
//     with calibrated, traceable thermometers; daily min/max log review.

const SECTIONS = [
  { id: "what", t: "What it is" },
  { id: "specs", t: "Required specs" },
  { id: "monitoring", t: "Monitoring" },
  { id: "excursions", t: "Excursions" },
  { id: "buying", t: "Buying checklist" },
] as const

export function MedicalLabFridgeGuidePageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Medical Refrigerator Guide"
      heroEyebrow="Specialty"
      heroTitlePre="The medical refrigerator your"
      heroTitleItalic="practice"
      heroTitlePost="actually needs."
      heroSubtitle="Vaccines, blood products, and compounded sterile preparations have non-negotiable storage requirements set by the CDC, FDA, AABB, and USP. A household fridge will fail an inspection on day one. Here's what regulators actually require."
      heroMeta={["⏱ ~7 min read", "CDC + FDA + NSF/ANSI 456"]}
      heroShare={<ToolShareBar title="Medical Refrigerator Guide" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            CDC vaccine storage band
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Refrigerated vaccines must hold{" "}
            <em className="italic text-[#ff9951]">2°C to 8°C</em> (36°F to 46°F) in a{" "}
            <em className="italic text-[#ff9951]">stand-alone</em>, purpose-built unit.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Refrigerator
              </div>
              <div className="text-[22px] font-black tracking-tight">2–8°C</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Freezer
              </div>
              <div className="text-[22px] font-black tracking-tight">−50 to −15°C</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Can I store vaccines in a household refrigerator?",
          answer:
            "No. The CDC Vaccines for Children (VFC) program prohibits storage of vaccines in household-style combination refrigerator/freezer units, dormitory units, or bar fridges. Only purpose-built or pharmaceutical-grade stand-alone refrigerators and stand-alone freezers are acceptable. NSF/ANSI 456-listed units meet the standard.",
        },
        {
          question: "What temperature do vaccines need?",
          answer:
            "Refrigerated vaccines: 2°C to 8°C (36°F to 46°F), with the target set point typically 5°C (40°F). Frozen vaccines: −50°C to −15°C (−58°F to +5°F). Source: CDC VFC Storage and Handling Toolkit.",
        },
        {
          question: "What's required for blood and plasma storage?",
          answer:
            "Whole blood and red cells: 1°C to 6°C, per AABB Standards and FDA 21 CFR 600.15. Fresh frozen plasma: at or below −18°C, with most blood banks targeting −30°C or colder. Continuous temperature monitoring with audible alarms is mandatory under AABB accreditation.",
        },
        {
          question: "Do I need continuous temperature monitoring?",
          answer:
            "Yes. CDC requires a digital data logger (DDL) with a current and valid Certificate of Calibration Testing for vaccine storage. Blood banks under AABB accreditation require continuous temperature recording. CLIA/CAP labs require daily min/max documentation with calibrated, NIST-traceable thermometers.",
        },
        {
          question: "How long do I have if a unit goes out of range?",
          answer:
            "It depends on the product. CDC requires that any out-of-range vaccine excursion be documented and the manufacturer contacted before use — there's no universal grace period. Blood products have AABB-defined limits. The single most important step in any excursion: don't discard or use the product until you've called the manufacturer or transfusion service for guidance.",
        },
      ]}
      relatedTools={[
        { title: "Temperature Settings Guide", category: "Temperature", description: "USDA + CDC reference set points.", href: "/tools/refrigerator-temperature-settings-guide", time: "~5 min" },
        { title: "Temperature Alarm Guide", category: "Temperature", description: "How alarm thresholds work.", href: "/tools/refrigerator-temperature-alarm-guide", time: "~6 min" },
        { title: "Door Seal Test", category: "Maintenance", description: "Verify the gasket holding your set point.", href: "/tools/refrigerator-door-seal-test", time: "~3 min" },
        { title: "Power Outage Food Safety", category: "Food Safety", description: "Same logic, applied to food.", href: "/tools/power-outage-food-safety-guide", time: "~5 min" },
      ]}
      cta={{
        title: "Not buying a medical unit today?",
        body: "If you're sourcing a household or office refrigerator instead, browse our counter-depth refrigerators — closer in footprint to undercounter medical units.",
        buttonLabel: "Browse counter-depth refrigerators",
        buttonHref: "/counter-depth-refrigerators",
      }}
    >
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          <article>
            <GuideTool
              eyebrow="Interactive compliance check"
              title="Is your storage setup compliant?"
              intro="What you store and how you monitor it decide the bar. Three questions against the CDC storage guidance."
              questions={[
                            {
                              id: "what",
                              label: "What are you storing?",
                              options: [
                                { value: "vaccines", label: "Vaccines" },
                                { value: "meds", label: "Insulin / medication" },
                                { value: "samples", label: "Samples / reagents" }
                              ],
                            },
                            {
                              id: "unit",
                              label: "In what unit?",
                              options: [
                                { value: "medical", label: "Purpose-built medical unit" },
                                { value: "household", label: "Household refrigerator" },
                                { value: "mini", label: "Dorm-style mini fridge" }
                              ],
                            },
                            {
                              id: "monitor",
                              label: "How is temperature monitored?",
                              options: [
                                { value: "ddl", label: "Digital data logger" },
                                { value: "minmax", label: "Min/max thermometer" },
                                { value: "none", label: "No dedicated monitor" }
                              ],
                            }
              ]}
              compute={(a) => {
                            if (a.what === "vaccines" && (a.unit === "mini" || a.monitor === "none")) {
                              return { tone: "bad", verdict: "Not compliant for vaccines", detail: "CDC guidance is explicit: no dorm-style units for vaccine storage, a stable 36\u201346\u00b0F range, and continuous monitoring with a digital data logger. A mini fridge's temperature swings and a monitor-free setup both fail that bar." }
                            }
                            if (a.unit === "medical" && a.monitor === "ddl") {
                              return { tone: "good", verdict: "Compliant setup", detail: "Purpose-built unit plus a digital data logger is exactly the CDC-recommended configuration. Keep water bottles in the door and dead space to buffer temperature, and log daily min/max checks." }
                            }
                            return { tone: "warn", verdict: "Workable \u2014 with upgrades", detail: "For personal insulin and medication a household refrigerator works if it holds 36\u201346\u00b0F away from vents and door \u2014 add at least a min/max thermometer, and never store medication in the door. For anything clinical, step up to a purpose-built unit with a data logger." }
                          }}
              ctaLabel="Check my setup"
              footnote="Thresholds: CDC vaccine storage and handling guidance, detailed below."
            />
            <section id="what" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                What counts as a medical refrigerator
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                A medical or pharmaceutical-grade refrigerator is engineered to hold a tight,
                uniform temperature inside a federally regulated band. It uses a forced-air
                evaporator to keep top-to-bottom temperature variance small, has a digital
                controller with audible high/low alarms, and is built to recover quickly after the
                door is opened.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                A household fridge does none of these reliably. Defrost cycles can drive interior
                temperature several degrees out of band; door bins swing well outside spec; and
                there&apos;s no audit trail when the regulator asks for one.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    CDC: stand-alone units only for vaccines
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    The VFC Storage and Handling Toolkit prohibits dormitory, bar, and household
                    combination refrigerator/freezer units for vaccine storage. Stand-alone
                    purpose-built or pharmaceutical-grade only. NSF/ANSI 456 listing meets the
                    requirement.
                  </p>
                </div>
              </div>
            </section>

            <section id="specs" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Required temperature bands
              </h2>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Product
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Band
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Authority
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Refrigerated vaccines", "2°C to 8°C (36–46°F)", "CDC VFC Toolkit"],
                      ["Frozen vaccines", "−50°C to −15°C (−58 to +5°F)", "CDC VFC Toolkit"],
                      ["Whole blood / red cells", "1°C to 6°C", "AABB / FDA 21 CFR 600.15"],
                      ["Fresh frozen plasma", "≤ −18°C (typ. −30°C)", "AABB Standards"],
                      ["Compounded sterile preps", "2°C to 8°C", "USP <797>"],
                      ["Insulin (in-use, refrigerated)", "2°C to 8°C", "FDA / manufacturer labels"],
                      ["Many lab reagents", "2°C to 8°C", "Manufacturer SDS / IFU"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[1]}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-600">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[13px] text-gray-500 mt-4">
                Always defer to the product label and the most current edition of the cited
                standard. Manufacturer instructions for use override generic bands.
              </p>
            </section>

            <section id="monitoring" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Temperature monitoring requirements
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Digital data logger with current calibration", d: "CDC requires a DDL with a Certificate of Calibration Testing that's current and traceable to NIST. The probe should sit in a glycol-buffered bottle inside the central storage area — not on the wall and not in a door bin." },
                  { n: "02", t: "Min / max readings logged daily", d: "Read and document the minimum and maximum temperature reached since the last check, at least once per workday. The log must include date, time, temperatures, initials, and any action taken." },
                  { n: "03", t: "Audible high/low alarms", d: "Alarms must trigger before product reaches an out-of-range condition long enough to harm potency. AABB requires audible alarms on blood-bank refrigerators with response procedures documented." },
                  { n: "04", t: "Backup power or escalation plan", d: "Have a written plan for power loss: who responds, where product moves, and how transport is temperature-controlled. CDC requires emergency vaccine storage and transport procedures be documented in advance." },
                  { n: "05", t: "Annual recalibration", d: "Calibration certificates expire — typically every 1–2 years. Schedule recalibration with a NIST-traceable provider before the certificate lapses." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="excursions" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Handling a temperature excursion
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                A temperature excursion is any reading outside the required band — even briefly.
                CDC&apos;s direction is unambiguous: the product is not used or discarded until a
                qualified party has reviewed the event.
              </p>
              <div className="space-y-3">
                {[
                  { t: "Label and isolate", d: "Mark the affected product 'DO NOT USE' and move it to a working unit operating in spec." },
                  { t: "Document the event", d: "Capture date, time, duration out of range, min/max temperatures, and root cause if known." },
                  { t: "Contact the manufacturer", d: "For vaccines, call each manufacturer's stability hotline. For blood products, contact the transfusion service. They issue the disposition decision." },
                  { t: "Report and root-cause", d: "Notify your VFC coordinator (vaccines), accrediting body (blood bank), or quality officer. Identify cause — door left open, defrost cycle, compressor fault, power loss — and corrective action." },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(255,153,81,0.04)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#ff9951" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="buying" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Buying checklist
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Stand-alone refrigerator (not combo with freezer) for vaccines",
                  "NSF/ANSI 456 listing for vaccine storage",
                  "Forced-air interior — uniform temperature top to bottom",
                  "Microprocessor controller with min/max display",
                  "Audible high/low alarm with door-ajar alarm",
                  "Lockable door (controlled-substance/regulated product workflows)",
                  "Glycol bottle and NIST-traceable digital data logger",
                  "Battery or backup power for alarm and logger",
                  "Service contract or local-stocked replacement parts",
                  "Adequate clearance for condenser airflow per spec",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>
          </article>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Closest residential equivalent"
          title="Counter-depth refrigerators"
          description="If you don't need a regulated medical unit, counter-depth models match the footprint of pharma undercounter refrigerators."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
